import React, { useState, useEffect } from 'react';
import domtoimage from 'dom-to-image';
import styled from 'styled-components';
import { inject } from 'mobx-react';
import { displayError } from '@aws-ee/base-ui/dist/helpers/notification';
import moment from 'moment';
import { getMyCertificates } from '../../../../helpers/api';
import certificatePathBackgroundImg from '../../../../images/certificate-path-background.svg';
import certificateBackgroundImg from '../../../../images/certificate-background.svg';
import certificateBadgeImg from '../../../../images/certificate-badge.svg';
import certificateSignatureImg from '../../../../images/certificate-signature.svg';
import stanfordGeneticsLogoImg from '../../../../images/stanford-genetics-logo.svg';
import ProgressEnum from '../../../../models/learning/ProgressEnum';

const Container = styled.div`
  height: 450px;
  margin-top: 140px;
  display: flex;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 410px;
`;

const CertificateExam = styled.p`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.08em;
  color: #d1660f;
  margin: 0px 0px 8px 0px;
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 24px;
  line-height: 135%;
  color: #2e2d29;
  margin: 0px 0px 32px 0px;
`;

const Description = styled.p`
  font-weight: 450;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: #2e2d29;
  margin: 0px 0px 32px 0px;
`;

const StartExamButton = styled.button`
  width: fit-content;
  padding: 12px 16px;
  background: #2e2d29;
  border-radius: 4px;
  font-family: 'IBM Plex Mono';
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #fafafa;
  border: none;
  cursor: pointer;

  :disabled {
    background: #969dab;
    pointer-events: none;
  }
`;

const Subtitle = styled.p`
  font-weight: 450;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: #969dab;
  margin: 16px 0px 0px 0px;
`;

const AccessCertificateButton = styled.button`
  width: fit-content;
  border: none;
  padding: 12px 16px;
  height: 46px;
  background: #f9a44a;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #ffffff;
  cursor: pointer;
`;

const CertificateContainer = styled.div`
  background-image: url(${certificatePathBackgroundImg});
  height: 780px;
  width: 780px;
  margin-left: 60px;
  background-repeat: no-repeat;
  background-clip: content-box;
  display: flex;
  justify-content: center;
`;

const Certificate = styled.div`
  position: relative;
  width: 566px;
  /* A recipient whose name wraps to two lines needs more than 400px. Fixed height let the signature
     block spill past the bottom border, which the old offsetHeight+100 capture slack exposed below it.
     min-height keeps every certificate that fits today pixel-identical and lets longer ones grow.
     The QR is absolutely positioned against the bottom edge, so when present the in-flow content
     needs enough bottom padding to clear it. */
  min-height: 400px;
  /* CertificateContainer is a 780px-tall flex parent, so its default align-items: stretch would
     size this frame to the full 780px now that the height is no longer fixed -- leaving a large
     gap under the signature block and dragging the absolutely-positioned QR down with it.
     flex-start restores content-driven height while min-height still guards the short case. */
  align-self: flex-start;
  padding-bottom: ${p => (p.hasQr ? '76px' : '20px')};
  border-top: 10px solid #8c1515;
  border-bottom: 10px solid #8c1515;
  border-radius: 8px;
  margin-top: 20px;
  background-image: url(${certificateBackgroundImg});
  background-color: #ffffff;
  /* tiles down a grown frame instead of leaving the extra height blank */
  background-repeat: repeat-y;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
`;

const CertificateHeader = styled.div`
  width: 100%;
  align-items: center;
  display: grid;
  grid-template-columns: 1fr repeat(3, auto) 1fr;
  grid-column-gap: 5px;
  justify-items: center;
`;

const CertificateLogo = styled.img`
  width: 216px;
  height: 27px;
  grid-column-start: 4;
`;

const CertificateBadge = styled.img`
  width: 80.13px;
  height: 80.13px;
  margin: 10px 20px 0px auto;
`;

const CertificateBody = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0px 123px;
  text-align: center;
`;

const CertificateTitle = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 700;
  font-size: 16px;
  line-height: 130%;
  letter-spacing: -0.01em;
  color: #2e2d29;
  opacity: 0.9;
  padding: 0px 25px;
  margin: 3px 0px 0px 0px !important;
  text-align: center;
`;

const ProgramDuration = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 700;
  font-size: 8px;
  line-height: 130%;
  text-align: center;
  letter-spacing: -0.01em;
  color: #2f2424;
  opacity: 0.9;
  margin: 8px 0px 0px 0px;
`;

const CertificationDate = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 600;
  font-size: 8px;
  line-height: 135%;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #53565a;
  opacity: 0.5;
  margin: 8px 0px 0px 0px;
`;

const UserName = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 900;
  font-size: 32px;
  line-height: 126%;
  text-align: center;
  color: #2f2424;
  margin: 16px 0px 0px 0px;
`;

const IssuerDirectorSignature = styled.img`
  width: 100%;
  height: 40px;
  margin: 40px 0px 0px 0px;
  border-bottom: 0.500832px solid rgba(83, 86, 90, 0.25);
`;

const IssuerDirectorWithDegree = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 700;
  font-size: 8px;
  line-height: 135%;
  color: #53565a;
  opacity: 0.5;
  margin: 12px 0px 6px 0px;
`;

const IssuerDirectorTitle = styled.p`
  font-family: 'Source Sans Pro';
  font-weight: 400;
  font-size: 8px;
  line-height: 135%;
  color: #53565a;
  opacity: 0.5;
  margin: 0px 0px 2px 0px;
  width: 100%;
`;

const CertificateVerify = styled.div`
  position: absolute;
  left: 24px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const CertificateVerifyText = styled.div`
  font-family: 'Source Sans Pro';
  font-size: 6.5px;
  line-height: 1.5;
  text-align: left;
  color: #53565a;
  opacity: 0.65;
`;

const CertificatePath = ({ learningId, path, disabled, learningStore, userStore }) => {
  const [buttonInProgress, setButtonInProgress] = useState(false);
  const [award, setAward] = useState(null);

  const module = path.modules[0];
  const currentUser = userStore.user;

  // Look up this certificate's verification record (id + QR) so it can be printed onto the downloadable
  // certificate. Best-effort: if it is missing (e.g. an older completion not yet backfilled) or the call
  // fails, the certificate still renders and downloads exactly as before, just without the QR block.
  useEffect(() => {
    let cancelled = false;
    if (module.progress !== ProgressEnum.Completed) return undefined;
    getMyCertificates()
      .then(list => {
        if (cancelled) return;
        // NOTE: the `learningId` prop here is actually the userLearning id (LearningMenu passes
        // selectedUserLearning.id). Match on the award's natural-key components (userLearningId + pathId),
        // which are unique per enrollment — matching on the learning-definition id or pathId alone is wrong.
        const match = (list || []).find(
          a => a.type === 'completion' && a.userLearningId === learningId && a.pathId === path.id && a.status === 'ISSUED',
        );
        setAward(match || null);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [learningId, path.id, module.progress]);

  const capitalizeFirstLetter = value => value.charAt(0).toUpperCase() + value.slice(1);

  const handleStart = async () => {
    setButtonInProgress(true);
    try {
      if (module.progress === ProgressEnum.NotStarted)
        await learningStore.startUserLearningModule(learningId, path.id, module.id);
    } catch (error) {
      displayError(error);
    }
    setButtonInProgress(false);
    window.open(`/learn/${learningId}/path/${path.id}/${module.id}/quiz`, '_blank').focus();
  };

  const handleDownloadCertificate = async () => {
    const element = document.getElementById('certificate');
    const scale = 2;
    const style = {
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width: `${element.offsetWidth}px`,
      height: `${element.offsetHeight}px`,
      // The clone inherits the frame's computed margin-top, and a margin sits outside the border box
      // that transform-origin scales -- so without this the frame is drawn 20px down the canvas and
      // the bottom border falls off the end. The capture is the frame's border box, not its placement.
      margin: 0,
    };
    const param = {
      width: element.offsetWidth * scale,
      // Exactly the frame. The old `+ 100` existed to reveal content that overflowed the fixed-height
      // frame; now that the frame grows to fit, that slack is just transparent canvas below the border.
      height: element.offsetHeight * scale,
      // The 8px border-radius leaves the four corners transparent, so paint the canvas opaque.
      bgcolor: '#ffffff',
      quality: 1,
      style,
    };

    domtoimage.toPng(element, param).then(dataUrl => {
      const link = document.createElement('a');
      link.download = module.name;
      link.href = dataUrl;
      link.click();
    });
  };

  return (
    <Container>
      <Info>

      {module.progress === ProgressEnum.Completed ? (
        <>
        <CertificateExam>CONGRATULATIONS</CertificateExam>
        <Title>
          You earned the certificate! 🎉
        </Title>
        <Description>
          We are happy for your accomplishment! We hope the 
          time you spent on our program helps pave the journey 
          for your future achievements.

          <br />
          <br />
          At last, we have a few kind asks from you:
          <ol>
            <li>Please help our team learn which part of the program is most useful to you via this <a href="https://stanfordmedicine.qualtrics.com/jfe/form/SV_6yReVVyi76GwKVw" target="_blank" rel="noopener noreferrer" style={{ color: 'orange', textDecoration: 'underline' }}>5-min survey</a>.</li>
            <li>If you believe your friends and colleagues on LinkedIn can benefit from this program, please help us spread the words by sharing your certificate on LinkedIn, and tagging <a href="https://www.linkedin.com/company/deep-data-stanford/" target="_blank" rel="noopener noreferrer" style={{ color: 'orange', textDecoration: 'underline' }}>Stanford Deep Data Research Center</a>.</li>
          </ol>          
        </Description>
        </>
        ) : (
        <>
          <CertificateExam>CERTIFICATE EXAM</CertificateExam>
          <Title>
            Get certified by the {module.issuer.department} at {module.issuer.name}
          </Title>
          <Description>
            The certificate exam will cover all the topics in each milestone. You are going to receive the certificate 
            after you respond to more than {module.minValidPercentage}% of the questions correctly.
            <br />
            <br />
            You can take the exam {module.maxNumberOfTrials} times. Make sure to spend ample time reviewing the content,
            and practice exercises before each attempt.
          </Description>
          </>
        )}
        {module.progress === ProgressEnum.Completed ? (
          <AccessCertificateButton type="button" onClick={handleDownloadCertificate}>
            {' '}
            <span role="img" aria-label="rocket">
              ‍{' '}
            </span>
            🎓 Access Certificate
          </AccessCertificateButton>
        ) : (
          <StartExamButton
            type="button"
            disabled={disabled || buttonInProgress || module.numberOfTrials >= module.maxNumberOfTrials}
            onClick={handleStart}
          >
            <span role="img" aria-label="rocket">
              🚀{' '}
            </span>
            Start Exam
          </StartExamButton>
        )}
        {module.progress !== ProgressEnum.Completed && module.numberOfTrials < module.maxNumberOfTrials ? (
          <Subtitle>Carefully study before you make any of the {module.maxNumberOfTrials} attempts.</Subtitle>
        ) : (
          <></>
        )}
      </Info>
      <CertificateContainer>
        <Certificate id="certificate" hasQr={!!(award && award.qrCodeDataUri)}>
          <CertificateHeader>
            <CertificateLogo src={stanfordGeneticsLogoImg} alt="stanford-genetic-logo" />
            <CertificateBadge src={certificateBadgeImg} alt="certificate-badge" />
          </CertificateHeader>
          <CertificateBody>
            <CertificateTitle>{module.name}</CertificateTitle>
            <ProgramDuration>Program Duration: {module.programDuration / 60 / 60} hours</ProgramDuration>
            {module.progress === ProgressEnum.Completed ? (
              <>
                <CertificationDate>
                  Certificate of Completion granted on {moment(module.updatedAt).format('MMMM DD, yyyy')} to
                </CertificationDate>
                <UserName>{`${capitalizeFirstLetter(currentUser.firstName)} ${capitalizeFirstLetter(
                  currentUser.lastName,
                )}`}</UserName>
                <IssuerDirectorSignature src={certificateSignatureImg} alt={module.issuer.director.name} />
                <IssuerDirectorWithDegree>
                  <span style={{ fontWeight: 'bold' }}>{module.issuer.director.name}</span>, {module.issuer.director.degree}
                </IssuerDirectorWithDegree>
                
                {module.issuer.director.titles.map(t => (
                  <IssuerDirectorTitle>{t}</IssuerDirectorTitle>
                ))}
                {award && award.qrCodeDataUri ? (
                  <CertificateVerify>
                    <img src={award.qrCodeDataUri} alt="Verify this certificate" width="52" height="52" />
                    <CertificateVerifyText>
                      Verify this certificate at
                      <br />
                      dataocean.stanford.edu/verify-certificate
                      <br />
                      ID: {award.certificateId}
                    </CertificateVerifyText>
                  </CertificateVerify>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <></>
            )}
          </CertificateBody>
        </Certificate>
      </CertificateContainer>
    </Container>
  );
};

export default inject('userStore', 'learningStore')(CertificatePath);
